import { diff } from 'deep-diff';
import { capitalize } from './string.esm.js';

const diffDynamicPageVersions = (oldVersion, newVersion) => {
    const dynamicPagesDiff = getDynamicPagesDiff(oldVersion.dynamicPage, newVersion.dynamicPage);
    const variantsDiff = getVariantsDiff(oldVersion.variants, newVersion.variants);
    const websitesDiff = getWebsitesDiff(oldVersion.websites, newVersion.websites);
    const formsDiff = getFormsDiff(oldVersion.forms, newVersion.forms);
    const dynamicPageConfigDiff = getDynamicPageConfigDiff(oldVersion.dynamicPage.config || {}, newVersion.dynamicPage.config || {});
    const dynamicPageBrandDiff = getDynamicPageBrandDiff(oldVersion.dynamicPage.brand || {}, newVersion.dynamicPage.brand || {});
    const accountConfigDiff = getAccountConfigDiff(oldVersion.accountConfig || {}, newVersion.accountConfig || {});
    const accountBrandDiff = getAccountBrandDiff(oldVersion.accountBrand || {}, newVersion.accountBrand || {});
    return [
        ...dynamicPagesDiff,
        ...variantsDiff,
        ...websitesDiff,
        ...formsDiff,
        ...dynamicPageConfigDiff,
        ...dynamicPageBrandDiff,
        ...accountConfigDiff,
        ...accountBrandDiff,
    ];
};
const getEntityDiff = function (oldEntities = [], newEntities = [], entityId) {
    const oldEntitiesMap = new Map(oldEntities.map((entity) => [entityId(entity), entity]));
    const newEntitiesMap = new Map(newEntities.map((entity) => [entityId(entity), entity]));
    const added = [];
    newEntities.forEach((newEntity) => {
        if (!oldEntitiesMap.has(entityId(newEntity))) {
            added.push(newEntity);
        }
    });
    const removed = [];
    oldEntities.forEach((oldEntity) => {
        if (!newEntitiesMap.has(entityId(oldEntity))) {
            removed.push(oldEntity);
        }
    });
    const changed = [];
    newEntities.forEach((entity) => {
        const oldEntity = oldEntitiesMap.get(entityId(entity));
        if (!oldEntity)
            return;
        if (JSON.stringify(oldEntity) !== JSON.stringify(entity)) {
            changed.push({ new: entity, old: oldEntity });
        }
    });
    return { added, removed, changed };
};
const getDynamicPagesDiff = (oldDynamicPage, newDynamicPage) => {
    const metadataFields = [
        'name',
        'description',
        'goal',
        'tags',
    ];
    const partialOld = Object.fromEntries(Object.entries(oldDynamicPage).filter(([key]) => metadataFields.includes(key)));
    const partialNew = Object.fromEntries(Object.entries(newDynamicPage).filter(([key]) => metadataFields.includes(key)));
    const diffs = [];
    const entitiesDiff = diff(partialOld, partialNew);
    if (entitiesDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'dynamicPage',
            explanation: 'Dynamic Page changed',
            id: newDynamicPage.id,
            name: newDynamicPage.name,
            diff: entitiesDiff,
        });
    }
    const pathDiff = diff(oldDynamicPage.path, newDynamicPage.path);
    if (pathDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'dynamicPage',
            explanation: `Dynamic Page path changed to /"${newDynamicPage.path}".`,
            id: newDynamicPage.id,
            name: newDynamicPage.name,
            diff: pathDiff,
        });
    }
    const { added: addedAlternatePathes, removed: removedAlternatePathes } = getEntityDiff(oldDynamicPage.alternatePathes, newDynamicPage.alternatePathes, (path) => path);
    addedAlternatePathes.forEach((path) => {
        diffs.push({
            type: 'added',
            entity: 'dynamicPageAlternatePath',
            explanation: `Added alternate path /"${path}".`,
            id: newDynamicPage.id,
            name: newDynamicPage.name,
        });
    });
    removedAlternatePathes.forEach((path) => {
        diffs.push({
            type: 'removed',
            entity: 'dynamicPageAlternatePath',
            explanation: `Removed alternate path /"${path}".`,
            id: newDynamicPage.id,
            name: newDynamicPage.name,
        });
    });
    const { added: addedDomains, removed: removedDomains } = getEntityDiff(oldDynamicPage.domains, newDynamicPage.domains, (d) => d);
    addedDomains.forEach((domain) => {
        diffs.push({
            type: 'added',
            entity: 'domain',
            explanation: `Added domain "${domain}".`,
            id: domain,
            name: domain,
        });
    });
    removedDomains.forEach((domain) => {
        diffs.push({
            type: 'removed',
            entity: 'domain',
            explanation: `Removed domain "${domain}".`,
            id: domain,
            name: domain,
        });
    });
    return diffs;
};
const getVariantsDiff = (oldVariants, newVariants) => {
    const diffs = [];
    const { added, removed, changed } = getEntityDiff(oldVariants, newVariants, (variant) => variant.id);
    changed.forEach(({ old: oldEntity, new: newEntity }) => {
        if (oldEntity.status !== newEntity.status) {
            diffs.push({
                type: 'changed',
                entity: 'variant',
                explanation: `Variant ${newEntity.name} status changed to ${capitalize(newEntity.status)}`,
                id: newEntity.id,
                name: newEntity.name,
                diff: [
                    {
                        kind: 'E',
                        path: ['status'],
                        lhs: oldEntity.status,
                        rhs: newEntity.status,
                    },
                ],
            });
            return;
        }
        const { added, removed } = getEntityDiff(oldEntity.websiteIds, newEntity.websiteIds, (websiteId) => websiteId);
        if (added.length) {
            diffs.push({
                type: 'added',
                entity: 'variant',
                explanation: `New experiment added to: "${newEntity.name}".`,
                id: newEntity.id,
                name: newEntity.name,
            });
        }
        if (removed.length) {
            diffs.push({
                type: 'removed',
                entity: 'variant',
                explanation: `Experiment removed from: "${newEntity.name}".`,
                id: newEntity.id,
                name: newEntity.name,
            });
        }
        const nameDiff = diff(oldEntity.name, newEntity.name);
        if (nameDiff?.length) {
            diffs.push({
                type: 'changed',
                entity: 'variant',
                explanation: `Name changed from "${oldEntity.name}" to "${newEntity.name}".`,
                id: newEntity.id,
                name: newEntity.name,
                diff: nameDiff,
            });
        }
        const descriptionDiff = diff(oldEntity.description, newEntity.description);
        if (descriptionDiff?.length) {
            diffs.push({
                type: 'changed',
                entity: 'variant',
                explanation: `Description changed from "${oldEntity.description}" to "${newEntity.description}".`,
                id: newEntity.id,
                name: newEntity.name,
                diff: descriptionDiff,
            });
        }
        const entitiesDiff = diff(oldEntity.constraints, newEntity.constraints);
        if (entitiesDiff?.length) {
            diffs.push({
                type: 'changed',
                entity: 'variant',
                explanation: `Constraints changed in "${newEntity.name}".`,
                id: newEntity.id,
                name: newEntity.name,
                diff: entitiesDiff,
            });
        }
    });
    return [
        ...diffs,
        ...added.map((variant) => ({
            type: 'added',
            entity: 'variant',
            explanation: `Added variant "${variant.name}".`,
            id: variant.id,
            name: variant.name,
        })),
        ...removed.map((variant) => ({
            type: 'removed',
            entity: 'variant',
            explanation: `Removed variant "${variant.name}".`,
            id: variant.id,
            name: variant.name,
        })),
    ];
};
const getWebsitesDiff = (oldWebsites, newWebsites) => {
    const diffs = [];
    const { changed } = getEntityDiff(oldWebsites, newWebsites, (website) => website.id);
    changed.forEach(({ old: oldEntity, new: newEntity }) => {
        const entitiesDiff = diff(oldEntity, newEntity);
        if (entitiesDiff?.length) {
            diffs.push({
                type: 'changed',
                entity: 'website',
                explanation: 'Page content changed',
                id: newEntity.id,
                name: 'Page',
                diff: entitiesDiff,
            });
        }
    });
    return diffs;
};
const getFormsDiff = (oldForms, newForms) => {
    const diffs = [];
    const { changed } = getEntityDiff(oldForms, newForms, (form) => form.id);
    changed.forEach(({ old: oldEntity, new: newEntity }) => {
        const entitiesDiff = diff(oldEntity, newEntity);
        if (entitiesDiff?.length) {
            diffs.push({
                type: 'changed',
                entity: 'form',
                explanation: `Form "${newEntity.name}" changed`,
                id: newEntity.id,
                name: newEntity.name,
                diff: entitiesDiff,
            });
        }
    });
    return diffs;
};
const getAccountConfigDiff = (oldConfig, newConfig) => {
    const diffs = [];
    const entitiesDiff = diff(oldConfig, newConfig);
    if (entitiesDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'accountConfig',
            explanation: 'Account config changed',
            id: 'default',
            name: 'Account Config',
            diff: entitiesDiff,
        });
    }
    return diffs;
};
const getAccountBrandDiff = (oldBrand, newBrand) => {
    const diffs = [];
    const entitiesDiff = diff(oldBrand, newBrand);
    if (entitiesDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'accountBrand',
            explanation: 'Account brand changed',
            id: 'default',
            name: 'Account Brand',
            diff: entitiesDiff,
        });
    }
    return diffs;
};
const getDynamicPageConfigDiff = (oldConfig, newConfig) => {
    const diffs = [];
    const entitiesDiff = diff(oldConfig, newConfig);
    if (entitiesDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'dynamicPageConfig',
            explanation: 'Dynamic Page config changed',
            id: 'default',
            name: 'Dynamic Page Config',
            diff: entitiesDiff,
        });
    }
    return diffs;
};
const getDynamicPageBrandDiff = (oldBrand, newBrand) => {
    const diffs = [];
    const entitiesDiff = diff(oldBrand, newBrand);
    if (entitiesDiff?.length) {
        diffs.push({
            type: 'changed',
            entity: 'dynamicPageBrand',
            explanation: 'Dynamic Page brand changed',
            id: 'default',
            name: 'Dynamic Page Brand',
            diff: entitiesDiff,
        });
    }
    return diffs;
};

export { diffDynamicPageVersions };
//# sourceMappingURL=dp-versions.esm.js.map
